import { Box } from "@mui/material";
import React from "react";
import theme from "Theme/AppTheme";
import AcxMainTextField, {
    AcxMainTextFieldRef,
} from "components/UI/AcxMainTextField";
import { UserWithFullName } from "components/Signals/Dashboard/DashboardMenu/ShareDialog/constants";
import { sxClsx } from "utils/styleClsx";
import { getMentionRegex } from "./mentionUtils";

type MentionTextFieldProps = React.ComponentProps<typeof AcxMainTextField> & {
    value: string;
    inputRef: React.RefObject<AcxMainTextFieldRef>;
    mentionSelections: UserWithFullName[];
};

const MentionTextField = ({
    value,
    inputRef,
    mentionSelections,
    sx,
    ...props
}: MentionTextFieldProps) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const highlightRef = React.useRef<HTMLDivElement>(null);
    const mentions = React.useMemo(() => {
        const ranges: { start: number; end: number }[] = [];
        mentionSelections.forEach(({ userId, fullName }) => {
            if (!userId || !fullName) return;
            for (const match of value.matchAll(getMentionRegex(fullName))) {
                const start = (match.index ?? 0) + match[1].length;
                ranges.push({
                    start,
                    end: start + match[0].length - match[1].length,
                });
            }
        });
        return ranges.sort((a, b) => a.start - b.start || b.end - a.end);
    }, [mentionSelections, value]);
    const hasMentions = mentions.length > 0;

    // Keep the native textarea for editing and mirror its text with colored spans.
    // Copy its actual metrics so wrapping, resizing, and scrolling stay aligned.
    const syncHighlight = React.useCallback(() => {
        const input = inputRef.current;
        const container = containerRef.current;
        const highlight = highlightRef.current;
        if (!input || !container || !highlight) return;
        const inputRect = input.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const computed = window.getComputedStyle(input);
        Object.assign(highlight.style, {
            top: `${inputRect.top - containerRect.top + input.clientTop}px`,
            left: `${inputRect.left - containerRect.left + input.clientLeft}px`,
            width: `${input.clientWidth}px`,
            height: `${input.clientHeight}px`,
            fontFamily: computed.fontFamily,
            fontSize: computed.fontSize,
            fontWeight: computed.fontWeight,
            lineHeight: computed.lineHeight,
            letterSpacing: computed.letterSpacing,
            padding: computed.padding,
        });
        highlight.scrollTop = input.scrollTop;
        highlight.scrollLeft = input.scrollLeft;
    }, [inputRef]);

    React.useLayoutEffect(() => {
        syncHighlight();
    }, [syncHighlight, value, hasMentions]);

    React.useLayoutEffect(() => {
        const input = inputRef.current;
        if (!input || !hasMentions) return;
        const observer = new ResizeObserver(syncHighlight);
        observer.observe(input);
        input.addEventListener("scroll", syncHighlight);
        return () => {
            observer.disconnect();
            input.removeEventListener("scroll", syncHighlight);
        };
    }, [inputRef, syncHighlight, hasMentions]);

    const highlightedText: React.ReactNode[] = [];
    let lastIndex = 0;
    mentions.forEach(({ start, end }) => {
        if (start < lastIndex) return;
        highlightedText.push(value.slice(lastIndex, start));
        highlightedText.push(
            <Box
                component="span"
                key={start}
                sx={{ color: theme.palette.primary.main }}
            >
                {value.slice(start, end)}
            </Box>,
        );
        lastIndex = end;
    });
    highlightedText.push(value.slice(lastIndex));

    return (
        <Box ref={containerRef} sx={{ position: "relative" }}>
            <AcxMainTextField
                {...props}
                ref={inputRef}
                value={value}
                sx={sxClsx(
                    sx,
                    hasMentions && {
                        "& textarea": {
                            color: "transparent",
                            caretColor: theme.palette.primary.main,
                        },
                    },
                )}
            />
            {hasMentions && (
                <Box
                    ref={highlightRef}
                    aria-hidden="true"
                    sx={{
                        position: "absolute",
                        pointerEvents: "none",
                        boxSizing: "border-box",
                        overflow: "hidden",
                        whiteSpace: "pre-wrap",
                        overflowWrap: "break-word",
                        color: theme.palette.text.primary,
                    }}
                >
                    {highlightedText}
                    {"\u200b"}
                </Box>
            )}
        </Box>
    );
};

export default MentionTextField;
